import type { SessionKind } from '@algo/shared';
import { useEffect, useReducer, useSyncExternalStore } from 'react';

/**
 * The running study timer.
 *
 * State lives in localStorage rather than React so that a page reload, a route
 * change or an accidental tab close cannot lose a session in progress. Elapsed
 * time is derived from wall-clock timestamps, never from a counter that ticks —
 * a backgrounded tab throttles its timers, but `Date.now()` never lies.
 */

const STORAGE_KEY = 'algo-os-study-timer';

export interface TimerState {
  topicId: string | null;
  kind: SessionKind;
  /** Epoch ms of the current run, or null while paused/stopped. */
  startedAt: number | null;
  /** Time banked by earlier runs before the current pause. */
  accumulatedMs: number;
}

const IDLE: TimerState = {
  topicId: null,
  kind: 'SOLVING',
  startedAt: null,
  accumulatedMs: 0,
};

function read(): TimerState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return IDLE;
    const parsed = JSON.parse(raw) as Partial<TimerState>;
    return {
      topicId: typeof parsed.topicId === 'string' ? parsed.topicId : null,
      kind: (parsed.kind ?? 'SOLVING') as SessionKind,
      startedAt: typeof parsed.startedAt === 'number' ? parsed.startedAt : null,
      accumulatedMs: typeof parsed.accumulatedMs === 'number' ? parsed.accumulatedMs : 0,
    };
  } catch {
    return IDLE;
  }
}

let state: TimerState = typeof window === 'undefined' ? IDLE : read();
const listeners = new Set<() => void>();

function emit(next: TimerState): void {
  state = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A full or blocked storage quota must not stop the timer from running.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Keep two open tabs in step.
  const onStorage = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY) {
      state = read();
      listener();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

const getSnapshot = (): TimerState => state;

/* --------------------------------- actions -------------------------------- */

export const timer = {
  start(): void {
    if (state.startedAt !== null) return;
    emit({ ...state, startedAt: Date.now() });
  },

  pause(): void {
    if (state.startedAt === null) return;
    emit({
      ...state,
      accumulatedMs: state.accumulatedMs + (Date.now() - state.startedAt),
      startedAt: null,
    });
  },

  reset(): void {
    emit({ ...state, startedAt: null, accumulatedMs: 0 });
  },

  setTopic(topicId: string | null): void {
    emit({ ...state, topicId });
  },

  setKind(kind: SessionKind): void {
    emit({ ...state, kind });
  },
};

/* --------------------------------- reading -------------------------------- */

export function elapsedMs(current: TimerState, now = Date.now()): number {
  const live = current.startedAt === null ? 0 : now - current.startedAt;
  return Math.max(0, current.accumulatedMs + live);
}

export function useTimerState(): TimerState {
  return useSyncExternalStore(subscribe, getSnapshot, () => IDLE);
}

/** Re-renders once a second while running, so the display stays live. */
export function useElapsedMs(current: TimerState): number {
  const [, tick] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (current.startedAt === null) return;
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [current.startedAt]);

  return elapsedMs(current);
}

export function formatClock(ms: number): string {
  const total = Math.floor(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}
